import { useState } from 'react'

import type { Message, MessageKind } from '../api/types'

/**
 * Hiding a part of the chat (1.93.0, #114).
 *
 * From use: a reader wants to see only the posts to everyone, or only what
 * was written between them and the author; the author, in the chat with one
 * person, only that exchange. **A view in the browser and no rule of the
 * server**: what anyone may see is decided by `Visibility`, and this only
 * leaves out rows the page already has. Nothing is asked of the server, and
 * nothing is stored there -- decided from use: per trip and chat, on the
 * device.
 */

/** What is shown: only the posts to everyone, or only the private exchange. */
export type FilterArt = 'beitraege' | 'privat'

/**
 * The chosen filter and the newest id the page knew when it was chosen --
 * everything hidden above `seit` is "new hidden", counted in the header.
 */
export type Chatfilter = { art: FilterArt; seit: number }

/**
 * A row of the private exchange: an answer or a message to one person. The
 * same rule as the private document (`PrivatbuchPage`), one place for both.
 */
export function istPrivat(m: { kind: MessageKind }): boolean {
  return m.kind !== 'broadcast'
}

/** Whether a row stands under this filter. Without one, every row does. */
export function sichtbar(m: { kind: MessageKind }, filter: Chatfilter | null): boolean {
  if (filter === null) return true
  return filter.art === 'privat' ? istPrivat(m) : !istPrivat(m)
}

/**
 * Which filters this chat offers -- none, where there is nothing to filter.
 *
 * - A reader's trip chat holds the posts, their own answers and what the
 *   author wrote to them: both ways of filtering.
 * - The author's chat with one person holds the posts as context and the
 *   exchange: only "private".
 * - The author's trip chat and the draft hold posts only.
 */
export function filterArten(rolle: string | undefined, withUser: number | undefined, entwurf: boolean): FilterArt[] {
  if (entwurf || rolle === undefined) return []
  if (rolle === 'sender') return withUser === undefined ? [] : ['privat']
  return withUser === undefined ? ['beitraege', 'privat'] : []
}

/**
 * How many hidden rows came after the filter was chosen -- not one's own,
 * not deleted, not empty (`zeigbar`).
 */
export function neuAusgeblendet(
  messages: Message[],
  filter: Chatfilter | null,
  zeigbar: (m: Message) => boolean,
): number {
  if (filter === null) return 0
  return messages.filter((m) => m.id > filter.seit && !m.own && !m.deleted && zeigbar(m) && !sichtbar(m, filter)).length
}

function schluessel(tripId: number, withUser: number | undefined): string {
  return `tb-chatfilter-${tripId}-${withUser ?? 'reise'}`
}

/** `localStorage` can throw (private window, storage off): then no filter. */
function lies(key: string): Chatfilter | null {
  try {
    const roh = localStorage.getItem(key)
    if (roh === null) return null
    const wert = JSON.parse(roh) as Partial<Chatfilter>
    if ((wert.art === 'beitraege' || wert.art === 'privat') && typeof wert.seit === 'number') {
      return { art: wert.art, seit: wert.seit }
    }
    return null
  } catch {
    return null
  }
}

function schreibe(key: string, wert: Chatfilter | null): void {
  try {
    if (wert === null) localStorage.removeItem(key)
    else localStorage.setItem(key, JSON.stringify(wert))
  } catch {
    // Not being able to store it is no reason not to filter now.
  }
}

/**
 * The filter of this trip and chat, and the way to set it.
 *
 * **Read anew when the chat changes.** `ThreadPage` stays mounted when the
 * address moves from one chat to the next; `usePreference` reads its key
 * once and would carry one chat's filter into the other. A filter this chat
 * does not offer (an old entry, a changed role) counts as none.
 */
export function useChatfilter(
  tripId: number,
  withUser: number | undefined,
  erlaubt: FilterArt[],
): [Chatfilter | null, (art: FilterArt | null, seit: number) => void] {
  const key = schluessel(tripId, withUser)
  const [stand, setStand] = useState(() => ({ key, wert: lies(key) }))
  const wert = stand.key === key ? stand.wert : lies(key)
  if (stand.key !== key) setStand({ key, wert })

  const setzen = (art: FilterArt | null, seit: number) => {
    const neu = art === null ? null : { art, seit }
    schreibe(key, neu)
    setStand({ key, wert: neu })
  }

  return [wert !== null && erlaubt.includes(wert.art) ? wert : null, setzen]
}
